// Radha — Collections, running on the direct path (now with writes).
//
// Reads are RLS-scoped SELECTs; writes go straight to PostgREST too, guarded in
// the database (migration 027): approve/reject a proposal, and send a nudge that
// the RBI trigger will block if it breaks the fair-collection rules. Kept plain
// on purpose — Milestone 4 brings the rich visuals; this proves the write path.
import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from "recharts";

import { useAccounts, useForecasts, useProposals } from "../hooks/useCollectionsDirect";
import {
  useDecideProposal, useSendNudge, useGenerateProposals, usePolishProposal, writeErrorMessage,
} from "../hooks/useCollectionsWrites";
import { useRole } from "../lib/authSupabase";
import ImportReceivables from "../components/ImportReceivables";
import { ExportButton } from "../components/TableSearch";
import { CreditLimits } from "../components/CreditLimits";
import { CustomFieldsRow } from "../components/CustomFieldsRow";
import { riskBandColor } from "../lib/statusColor";
import type { CollectionAccount, CollectionProposal } from "../types/collections";
import { fmtMoney as inr } from "../lib/money";
import { DebtorDnaPanel } from "../components/GlobalDeskPanels";


const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "10px 8px" };
const numTd: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const statusTd: React.CSSProperties = { ...td, textTransform: "capitalize" };
const pill = (c: string): React.CSSProperties => ({
  marginLeft: 6, padding: "1px 6px", fontSize: 11, color: c,
  border: `1px solid ${c}`, borderRadius: 6,
});
const btn = (bg: string): React.CSSProperties => ({
  padding: "5px 12px", fontSize: 13, color: "#fff", background: bg,
  border: 0, borderRadius: 7, cursor: "pointer",
});
const ghost: React.CSSProperties = {
  padding: "5px 12px", fontSize: 13, color: "var(--fg-muted)", background: "none",
  border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer",
};

function ForecastRow({ accountId }: { accountId: string }) {
  const { data, isLoading } = useForecasts(accountId);
  if (isLoading) return <span style={{ color: "var(--fg-muted)" }}>loading…</span>;
  const f = data?.[0];
  if (!f) return <span style={{ color: "var(--fg-muted)" }}>—</span>;
  return (
    <span>
      {inr(f.expected_recovery_amount)} in {f.expected_recovery_days ?? "?"}d
      <span style={{ color: "var(--fg-muted)" }}> ({Math.round(Number(f.confidence) * 100)}%)</span>
    </span>
  );
}

// One proposal, with its own decide/send mutations and result line.
function ProposalCard({ p, debtor }: { p: CollectionProposal; debtor: string }) {
  const decide = useDecideProposal();
  const nudge = useSendNudge();
  const polish = usePolishProposal();
  const { canWrite } = useRole();
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const decided = p.status !== "draft";

  const onPolish = () =>
    polish.mutate(p.id, {
      onSuccess: ({ provider }) => setNote({ ok: true, text: `Polished by ${provider}` }),
      onError: (e) => setNote({ ok: false, text: writeErrorMessage(e) }),
    });

  const onDecide = (decision: "approved" | "rejected") =>
    decide.mutate(
      { id: p.id, decision },
      {
        onSuccess: () => setNote({ ok: true, text: `Proposal ${decision}` }),
        onError: (e) => setNote({ ok: false, text: writeErrorMessage(e) }),
      },
    );

  const onSend = () =>
    nudge.mutate(
      {
        account_id: p.account_id,
        channel: p.recommended_channel ?? "whatsapp",
        language: p.recommended_language,
        body: p.draft_message ?? p.recommended_action,
      },
      {
        // The hour is local to the TENANT's zone, not IST (064) — label it with
        // the jurisdiction the conduct rule was applied under, not a fixed zone.
        onSuccess: (d) =>
          setNote({ ok: true, text: `Nudge sent (${d.local_hour_at_send}:00 local · ${d.conduct_country} rules)` }),
        onError: (e) => setNote({ ok: false, text: writeErrorMessage(e) }),
      },
    );

  const busy = decide.isPending || nudge.isPending || polish.isPending;

  return (
    <div className="sanctuary-card sanctuary-card-interactive sanctuary-reveal" style={{ padding: "13px 16px", borderRadius: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 600 }}>{debtor}</div>
          <div style={{ color: "var(--accent-purple)", fontSize: 12, margin: "2px 0 6px" }}>
            AI · {p.tier === 1 ? "LLM-polished" : "rule"} · {Math.round(Number(p.confidence) * 100)}% confidence
          </div>
          <div style={{ fontSize: 13 }}>{p.recommended_action}</div>
          {p.draft_message && (
            <div style={{ color: "var(--fg-muted)", fontSize: 13, fontStyle: "italic", marginTop: 4 }}>
              “{p.draft_message}”
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {p.recommended_channel && (
            <span style={pill("var(--accent-purple)")}>{p.recommended_channel}·{p.recommended_language}</span>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
        {decided ? (
          <span style={{ fontSize: 13, color: p.status === "approved" ? "var(--status-good)" : "var(--status-critical)" }}>
            {p.status}
          </span>
        ) : !canWrite ? (
          <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>Read-only</span>
        ) : (
          <>
            <button style={btn("var(--status-good)")} disabled={busy} onClick={() => onDecide("approved")}>Approve</button>
            <button style={ghost} disabled={busy} onClick={() => onDecide("rejected")}>Reject</button>
            <button style={btn("var(--accent-purple)")} disabled={busy} onClick={onSend}>Send nudge</button>
            {p.tier !== 1 && (
              <button style={btn("var(--accent-purple)")} disabled={busy} onClick={onPolish}>
                {polish.isPending ? "Polishing…" : "Polish with AI"}
              </button>
            )}
          </>
        )}
        {note && (
          <span style={{ marginLeft: "auto", fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>
            {note.text}
          </span>
        )}
      </div>
    </div>
  );
}

function ProposalsSection({ names }: { names: Map<string, string> }) {
  const { data, isLoading } = useProposals();
  const generate = useGenerateProposals();
  const { canWrite } = useRole();
  const [gen, setGen] = useState<string | null>(null);
  const open = (data ?? []).filter((p) => p.status === "draft");

  const onGenerate = () =>
    generate.mutate(undefined, {
      onSuccess: (n) => setGen(n > 0 ? `${n} new proposal${n > 1 ? "s" : ""}` : "None needed — all past-due accounts covered"),
      onError: (e) => setGen(writeErrorMessage(e)),
    });

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "0 0 8px" }}>
        <div style={{ font: "600 13px Inter", color: "var(--accent-purple)" }}>
          AI proposals awaiting your decision ({open.length})
        </div>
        {canWrite && (
          <button style={ghost} disabled={generate.isPending} onClick={onGenerate}>
            {generate.isPending ? "Generating…" : "Generate proposals"}
          </button>
        )}
        {gen && <span style={{ fontSize: 13, color: "var(--accent-purple)" }}>{gen}</span>}
      </div>
      {isLoading ? null : open.length === 0 ? (
        <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>No proposals right now.</div>
      ) : (
        <div className="sanctuary-stagger" style={{ display: "grid", gap: 10 }}>
          {open.map((p) => (
            <ProposalCard key={p.id} p={p} debtor={names.get(p.account_id) ?? "—"} />
          ))}
        </div>
      )}
    </div>
  );
}

// Panel only — the shell owns the header, tabs and sign-out.
export function CollectionsPanel() {
  const { data: accounts, isLoading, error } = useAccounts();
  const { canWrite } = useRole();
  const [open, setOpen] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("all");

  const totals = useMemo(() => {
    const list = accounts ?? [];
    return {
      count: list.length,
      outstanding: list.reduce((s, a) => s + Number(a.outstanding_amount), 0),
      samadhaan: list.filter((a) => a.samadhaan_eligible).length,
    };
  }, [accounts]);

  const names = useMemo(() => {
    const m = new Map<string, string>();
    (accounts ?? []).forEach((a) => m.set(a.id, a.debtor_name));
    return m;
  }, [accounts]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (accounts ?? []).filter((a) =>
      (band === "all" || a.risk_band === band) &&
      (needle === "" || a.debtor_name.toLowerCase().includes(needle)));
  }, [accounts, q, band]);

  if (isLoading) return <p style={{ padding: 24 }}>Loading accounts…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  return (
    <div>
      <div style={{ display: "flex", gap: 24, margin: "4px 0 20px", flexWrap: "wrap" }}>
        <Stat label="Accounts" value={String(totals.count)} />
        <Stat label="Outstanding" value={inr(totals.outstanding)} />
        <Stat label="Samādhān-eligible" value={String(totals.samadhaan)} />
      </div>

      <PortfolioChart accounts={accounts ?? []} />

      <CreditLimits canWrite={canWrite} />

      {canWrite && <ImportReceivables />}
      <ProposalsSection names={names} />

      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 12px", flexWrap: "wrap" }}>
        <input placeholder="Search debtor…" value={q} onChange={(e) => setQ(e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, minWidth: 200 }} />
        <select value={band} onChange={(e) => setBand(e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13 }}>
          <option value="all">All risk</option>
          <option value="critical">Critical</option><option value="high">High</option>
          <option value="medium">Medium</option><option value="low">Low</option>
        </select>
        <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>{filtered.length} of {(accounts ?? []).length}</span>
        <ExportButton name="Collections" rows={() => filtered.map((a: CollectionAccount) => ({
          Debtor: a.debtor_name, Outstanding: Number(a.outstanding_amount), DPD: a.days_past_due,
          Stage: a.stage, Risk: a.risk_band, MSME: a.is_msme ? "yes" : "no",
          "§16 eligible": a.samadhaan_eligible ? "yes" : "no",
        }))} />
      </div>

      <div className="sanctuary-scroll-x">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
            <th style={th}>Debtor</th><th style={{ ...th, textAlign: "right" }}>Outstanding</th><th style={{ ...th, textAlign: "right" }}>DPD</th>
            <th style={th}>Stage</th><th style={th}>Risk</th><th style={th}>Recovery forecast</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((a: CollectionAccount) => (
            <React.Fragment key={a.id}>
              <tr onClick={() => setOpen(open === a.id ? null : a.id)}
                style={{ borderTop: "1px solid var(--border)", cursor: "pointer" }}>
                <td style={td}>
                  {a.debtor_name}
                  {a.is_msme && <span style={pill("var(--status-good)")}>MSME</span>}
                  {a.samadhaan_eligible && <span style={pill("var(--status-warning)")}>§16</span>}
                </td>
                <td style={numTd}>{inr(a.outstanding_amount)}</td>
                <td style={numTd}>{a.days_past_due}</td>
                <td style={statusTd}>{a.stage.replace(/_/g, " ")}</td>
                <td style={{ ...statusTd, color: riskBandColor(a.risk_band), fontWeight: a.risk_band === "critical" || a.risk_band === "high" ? 600 : 400 }}>
                  {a.risk_band}
                </td>
                <td style={td}>{open === a.id ? <ForecastRow accountId={a.id} /> : "›"}</td>
              </tr>
              {open === a.id && (
                <tr>
                  <td colSpan={6} style={{ padding: "0 8px 8px" }}><CustomFieldsRow accountId={a.id} canWrite={canWrite} /></td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>

      <DebtorDnaPanel />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{label}</div>
      <div style={{ font: "600 20px Inter" }}>{value}</div>
    </div>
  );
}

// Outstanding by collection stage — where the money is stuck, from live accounts.
const STAGE_ORDER = ["current", "early", "mid", "late", "severe", "critical"];
const STAGE_COLOR: Record<string, string> = {
  current: "var(--status-good)", early: "#6FBF73", mid: "var(--status-warning)", late: "#E08A4B", severe: "var(--status-critical)", critical: "#A3271E",
};
function PortfolioChart({ accounts }: { accounts: CollectionAccount[] }) {
  const data = useMemo(() => {
    const by = new Map<string, number>();
    accounts.forEach((a) => by.set(a.stage, (by.get(a.stage) ?? 0) + Number(a.outstanding_amount)));
    return STAGE_ORDER.filter((s) => by.has(s)).map((s) => ({ stage: s, outstanding: by.get(s) ?? 0 }));
  }, [accounts]);
  if (data.length === 0) return null;
  return (
    <div style={{ height: 180, margin: "4px 0 22px" }}>
      <div style={{ font: "600 13px Inter", color: "var(--fg-muted)", marginBottom: 6 }}>Outstanding by stage</div>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-2)" vertical={false} />
          <XAxis dataKey="stage" tick={{ fontSize: 12, fill: "var(--fg-muted)" }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={(v) => inr(v)}
            tick={{ fontSize: 11, fill: "var(--fg-muted)" }} axisLine={false} tickLine={false} width={52} />
          <Tooltip formatter={(v: number) => inr(v)} cursor={{ fill: "var(--surface-2)" }}
            contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--fg)" }}
            labelStyle={{ color: "var(--fg)" }} itemStyle={{ color: "var(--fg)" }} />
          <Bar dataKey="outstanding" radius={[4, 4, 0, 0]}>
            {data.map((d) => <Cell key={d.stage} fill={STAGE_COLOR[d.stage] ?? "var(--accent-purple)"} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
